import { action, makeObservable } from 'mobx'
import { isMobile, isTablet } from 'react-device-detect'

import {
  CheckUserByPhoneNumberDocument,
  ResendInviteKeyDocument,
} from '~/client/graph/operations/generated/Auth.generated'
import Config, {
  configuredDesktopPort,
  configuredMobilePort,
} from '~/client/src/shared/Config'
import InfoCode from '~/client/src/shared/enums/InfoCode'
import ApiHelper, { IResponseData } from '~/client/src/shared/utils/ApiHelper'

import AuthEndpoints from '../enums/AuthEndpoints'
import { GrantTypesEnum } from '../enums/GrantTypes'
import Localization from '../localization/LocalizationManager'
import { ITenant } from '../stores/domain/Tenants.store'
import InitialState from '../stores/InitialState'
import { fetchWithTransientRetry } from '../utils/fetchWithTransientRetry'
import GraphExecutor from '../utils/GraphExecutor'
import { isTokenAboutToExpire } from '../utils/JwtHelper'
import { isLoginBundle } from '../utils/loginPortal'

// Do not translate. These are error messages from the API
const INVALID_PHONE_NUMBER = 'Invalid phone number'
const PHONE_NUMBER_NOT_FOUND_REGEX = /phone number \+?(\d+) not found/
// Auth refuses a sign-in method the account's domain does not allow with a 401, the same
// status as an unknown number, so the two are told apart by the message.
const LOGIN_METHOD_NOT_ALLOWED_REGEX = /is not allowed for this account/

// The refresh call runs behind the cross-tab Web Lock; bound it so a hung
// request can't hold the lock (and stall every other tab's refresh) until the
// browser's own network timeout. A timed-out refresh is treated like any other
// refresh failure by the caller.
const REFRESH_REQUEST_TIMEOUT_MS = 15_000

// Writing a password reaches STX Firebase and the tenant's, so it is slower
// than a read; this only has to be short enough that a hung request surfaces
// as an error while the user is still watching.
const SET_PASSWORD_TIMEOUT_MS = 30_000

// Portal avatars are up to 5 MB; 10s aborted multi-MB uploads on slow mobile
// links even though the server would accept them.
const PORTAL_AVATAR_UPLOAD_TIMEOUT_MS = 60_000

// Above the auth service's 10s per-tenant fan-out timeout (PortalProjectsService)
// plus projection upserts and transfer, so partial results still arrive.
const PORTAL_PROJECTS_TIMEOUT_MS = 20_000

// A name/phone save updates Firebase and then every linked tenant in turn (each
// can take up to 10s to connect), so 10s gave up on saves the server committed.
const PORTAL_PROFILE_SAVE_TIMEOUT_MS = 30_000

// The portal button waits on this before navigating; a stalled auth must not
// leave the click doing nothing, so it gives up and navigates without a code.
const HANDOFF_CODE_MINT_TIMEOUT_MS = 5_000

// Boot waits on the exchange; a stalled auth must fall through to login, not
// leave the loader up. No retry: the code is single-use.
const HANDOFF_CODE_EXCHANGE_TIMEOUT_MS = 10_000

// Starting or resending waits on the SMS provider accepting the message.
const PHONE_VERIFICATION_TIMEOUT_MS = 15_000

/**
 * An error whose message is localized copy meant for the user. Anything else
 * (status-code text, aborts, network failures) is technical, and UIs should
 * show their own localized fallback instead of its message.
 */
/** A 429's wait: how long, and which sends it holds. */
export interface IRetryHint {
  afterSeconds: number
  /** `account`: every send; `number`: sends to that number; `verification`: its resends. */
  scope: 'account' | 'number' | 'verification'
}

// A rate limit or a server failure says nothing about the reset code itself.
const isResetCodeRejected = (status: number): boolean =>
  status < 500 && status !== 408 && status !== 429

export class UserFacingError extends Error {
  public constructor(
    message: string,
    /** The auth service's error code, when it sent one. */
    public readonly code?: string,
    /** The wait the auth service asked for before retrying (a 429). */
    public readonly retry?: IRetryHint,
  ) {
    super(message)
    this.name = 'UserFacingError'
  }
}

// Error codes the auth service returns in a 4xx body, mapped to localized
// copy. Looked up lazily so the active language at throw time is used.
const SET_PASSWORD_ERROR_MESSAGES: Record<string, () => string> = {
  invalid_current_password: () =>
    Localization.translator.portalProfile.currentPasswordIncorrect,
  too_many_attempts: () =>
    Localization.translator.portalProfile.tooManyPasswordAttempts,
}

const PORTAL_PROFILE_ERROR_MESSAGES: Record<string, () => string> = {
  phone_only_sign_in_method: () =>
    Localization.translator.portalProfile.phoneOnlySignInMethod,
  phone_in_use: () => Localization.translator.portalProfile.phoneInUse,
  invalid_phone: () => Localization.translator.portalProfile.invalidPhone,
  invalid_profile: () => Localization.translator.portalProfile.invalidProfile,
}

interface IPhoneVerificationErrorBody {
  code?: string
  attemptsRemaining?: number
  retryAfterSeconds?: number
  scope?: IRetryHint['scope']
}

/** "Too many codes requested. Try again in …", in minutes or, for long waits, hours. */
export function tooManyCodesMessage(retryAfterSeconds: number): string {
  const t = Localization.translator.portalProfile
  return retryAfterSeconds >= 3600
    ? t.tooManyCodesRequestedHours(Math.ceil(retryAfterSeconds / 3600))
    : t.tooManyCodesRequested(Math.max(1, Math.ceil(retryAfterSeconds / 60)))
}

const PHONE_VERIFICATION_ERROR_MESSAGES: Record<
  string,
  (body: IPhoneVerificationErrorBody) => string
> = {
  invalid_code: body =>
    Localization.translator.portalProfile.invalidCode(
      body.attemptsRemaining ?? 0,
    ),
  code_expired: () => Localization.translator.portalProfile.codeExpired,
  phone_in_use: () =>
    Localization.translator.portalProfile.verificationPhoneInUse,
  invalid_phone: () => Localization.translator.portalProfile.invalidMobilePhone,
  too_many_requests: body => tooManyCodesMessage(body.retryAfterSeconds ?? 60),
  sms_unavailable: () => Localization.translator.portalProfile.smsUnavailable,
  unsupported_destination: () =>
    Localization.translator.portalProfile.unsupportedDestination,
}

// Rejects as soon as the signal aborts, instead of waiting out a promise that
// has its own, longer deadline. Without a signal the promise is returned
// untouched, so callers that never abort behave exactly as before.
function settleOrAbort<T>(
  promise: Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  if (!signal) {
    return promise
  }

  return Promise.race([
    promise,
    new Promise<never>((_resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }
      signal.addEventListener(
        'abort',
        () => reject(new DOMException('Aborted', 'AbortError')),
        { once: true },
      )
    }),
  ])
}

// STX1-1002: the refresh POST is fired blind when a dormant tab wakes, while the
// network path is often still re-establishing — so a sub-second blip costs the
// user their session. Retry more patiently than the foreground GET default
// (~1.75-3.5s of total backoff vs ~600ms): the recoveries measured in the logs
// landed ~2s out, and the whole loop still sits inside
// REFRESH_REQUEST_TIMEOUT_MS, which stays the authoritative bound.
const REFRESH_RETRY_MAX_ATTEMPTS = 4
const REFRESH_RETRY_BASE_DELAY_MS = 500

// Only idempotent verbs may be transient-retried; a missing method is GET.
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD'])
function isIdempotentMethod(method?: string): boolean {
  return IDEMPOTENT_METHODS.has((method ?? 'GET').toUpperCase())
}

interface IAuth {
  grantType: GrantTypes
}

export interface EmailAuth extends IAuth {
  email: string
  password: string
}

export interface PhoneAuth extends IAuth {
  phone: string
  oneTimeCode: string
  nonsense: string
}

export interface AuthMeResponse {
  /** User has a password provider linked. */
  hasPassword: boolean
  /** User has an email identifier on their account record. */
  hasEmail: boolean
  /** Tenant SSO policy blocks password sign-in — hide password UI. */
  isSSOEnforced: boolean
  /**
   * Password is the user's only available credential — no tenant they belong
   * to allows SSO for their domain. Frontend forces /save-password redirect
   * only when this is true. False for hybrid (SSO + password) and SSO-only
   * tenants — password is optional or disallowed there.
   */
  isPasswordRequired: boolean
}

export interface IPortalProjectDto {
  id: string
  name?: string
  location?: string
  lat?: number
  lng?: number
  logoUrl?: string
  headerImageUrl?: string
  code?: string
  companyLogoUrl?: string
  lastOpenedAt?: number
  /** The user's company on this project; absent when they have none. */
  companyName?: string | null
  /** Whether that company is the user's home company ("My Co."). */
  isHomeCompany?: boolean
}

export interface IPortalHomeCompanyDto {
  name: string
  logoUrl?: string | null
}

export interface IPortalTenantProjects {
  tenantHost: string
  tenantName?: string
  tenantDisplayName?: string | null
  tenantIconUrl?: string | null
  stale: boolean
  fetchedAt: string
  projects: IPortalProjectDto[]
}

export interface IPortalProjectsResponse {
  tenants: IPortalTenantProjects[]
  failedTenants: string[]
  /** Null when auth can't tell which company is the user's own. */
  homeCompany?: IPortalHomeCompanyDto | null
}

export interface IPortalProfileResponse {
  accountId?: string
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  employer?: string
  jobTitle?: string
  contactEmail?: string
  avatarUrl?: string
  phoneVerified?: boolean
}

export interface IPhoneVerificationResponse {
  status: 'code_sent' | 'already_verified'
  phoneNumber: string
  /** The fields below are present only when `status` is `code_sent`. */
  verificationId?: string
  expiresAt?: string
  resendAvailableAt?: string
}

export interface IPhoneVerificationConfirmResponse {
  phoneNumber: string
  phoneVerified: boolean
}

export interface IPortalProjectRequest {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  jobTitle?: string
  projectName?: string
  details?: string
}

export interface IPortalProfileUpdate {
  firstName?: string
  lastName?: string
  phoneNumber?: string
  employer?: string
  jobTitle?: string
  contactEmail?: string
}

interface IFreshToken {
  refreshToken: string
  grantType: GrantTypes
}

interface IAuthenticateOptions {
  additionalHeaders?: Record<string, string>
  /** Bounds the whole call; a fired timeout is terminal and never retried. */
  timeoutMs?: number
  /**
   * Retry transient failures (network rejection / 5xx). Opt-in, and today only
   * the refresh grant opts in (STX1-1002). Deliberately NOT set for interactive
   * login: this is a POST, so the safety argument is specific to refresh rather
   * than general. Auth already tolerates a refresh replay inside its 30s
   * rotation grace window (STX1-494) — it logs at Info and suppresses the reuse
   * alert — and STX1-545 removed family auto-revoke, so the worst case for a
   * retry whose predecessor did rotate is the same 401 the client already gets
   * when it re-attempts on its own seconds later.
   */
  retryTransient?: boolean
  /** When set, a 401 throws a UserFacingError with this copy instead of resolving null. */
  unauthorizedMessage?: () => string
}

interface IAuthenticateResult {
  accessToken: string
  refreshToken: string
  /** Absolute UTC expiry (ISO string) of `accessToken`. */
  accessTokenExpiresAt?: string
  /** Absolute UTC expiry (ISO string) of `refreshToken`. */
  refreshTokenExpiresAt?: string
}

type GrantTypes = `${GrantTypesEnum}`

// SSO Types
interface ISSOInitResponse {
  oAuthUrl: string
  state: string
}

/** Result of exchanging a session handoff code (STX1-495). */
export interface IHandoffCodeExchangeResult {
  /** The bearer sent with the exchange belongs to the code's account: keep that session. */
  reused: boolean
  accessToken?: string
  refreshToken?: string
}

export interface ISSOCallbackResult {
  success: boolean
  error?: string
  errorCode?: string
  authResult?: {
    accessToken: string
    refreshToken: string
  }
  tenant?: {
    id: string
    name: string
    hostname: string
    displayName?: string | null
    iconUrl?: string | null
  }
  tenants?: Array<{
    id: string
    name: string
    hostname: string
    displayName?: string | null
    iconUrl?: string | null
  }>
}

export default class ApiAuthService {
  public accessToken: string = null

  // The single refresh authority, wired by every container to
  // StruxHubAuthService.refreshAccessToken. ALL refresh triggers (REST 401
  // retry, GraphQL 401 retry, tenants lookup, …) funnel through it so they
  // share one cross-tab Web Lock + single-flight gate. The server rotates the
  // refresh token on every call, so a second, lock-less refresh path here
  // would let two contexts race and 401 the loser against an already-rotated
  // token — exactly the STX1-616 leak. There is therefore deliberately NO
  // independent refresh path in this service: `updateAccessToken` requires the
  // delegate (STX1-616 collapsed the former dual session store).
  private refreshAccessTokenDelegate: (() => Promise<string>) | null = null

  /**
   * Auth service base URL.
   * Uses the bundle-provided URL when available, otherwise tenant configuration.
   * Falls back to '/auth' (relative) where VirtualServer routes it.
   */
  private get authUrl(): string {
    return this.authExternalUrl || Config.AUTH_EXTERNAL_URL || '/auth'
  }

  public constructor(
    private readonly state: InitialState,
    private readonly graphExecutor: GraphExecutor,
    private readonly authExternalUrl?: string,
  ) {
    makeObservable(this)
  }

  /**
   * Run an authenticated fetch with a one-shot refresh-and-retry on 401.
   *
   * Background: until STX1-505, every authenticated method here took an
   * `accessToken` parameter from the caller, attached it as Bearer, and
   * threw on any non-OK response. When the token had expired (idle tab,
   * server-side rotation, …) the 401 propagated up to the bootstrap chain
   * which catches it and dispatches LOGOUT → bounce to login. The refresh
   * mechanism (`updateAccessToken` / `getValidAccessToken`) existed but
   * was never invoked from the request path, so even users with a valid
   * refresh token sitting in their session got kicked out.
   *
   * This helper centralises the right behaviour:
   *   1. Make the request with the caller-supplied token.
   *   2. On 401, attempt a refresh; if it produces a fresh token, retry
   *      the request once with the new Bearer.
   *   3. On second 401 (or if refresh itself fails), return the failure
   *      Response so the caller's existing error path runs — usually
   *      `throw response.status.toString()` → LOGOUT.
   *
   * Callers keep their `accessToken` parameter for backwards compatibility.
   */
  public async withRefreshRetry(
    url: string,
    buildOptions: (token: string) => RequestInit,
    initialToken: string,
  ): Promise<Response> {
    const initialOptions = buildOptions(initialToken)
    // Transient-network/5xx retry is opt-in for idempotent methods only
    // (STX1-622): retrying a non-idempotent POST after a lost response risks a
    // double-apply (e.g. /set-password). Treat a missing method as GET. The
    // 401 → refresh path below composes on top regardless of method — a blip
    // retries here only for idempotent verbs, an expired token still refreshes
    // once. buildOptions may carry a caller AbortSignal; honor it so a
    // caller-aborted request isn't retried.
    const idempotent = isIdempotentMethod(initialOptions.method)
    const signal = initialOptions.signal ?? undefined
    const runFetch = (options: RequestInit): Promise<Response> =>
      idempotent
        ? fetchWithTransientRetry(() => fetch(url, options), { signal })
        : fetch(url, options)

    const response = await runFetch(initialOptions)
    if (response.status !== 401) {
      return response
    }

    // A caller's abort has to cut this short too. The refresh carries its own
    // 15s bound, so awaiting it unconditionally lets a 401 arriving near the
    // caller's deadline overrun it by that much — and set-password holds the
    // prompt's controls shut for exactly as long as it is in flight. The
    // refresh itself is single-flight and keeps running for whoever else wants
    // it; we just stop waiting.
    let refreshed: string | null = null
    try {
      refreshed = await settleOrAbort(this.updateAccessToken(), signal)
    } catch {
      // Refresh-token call itself failed (invalid / network / 4xx), or the
      // caller aborted while it was in flight. Fall through and let the caller
      // treat this as a terminal 401.
    }
    if (!refreshed || refreshed === initialToken || signal?.aborted) {
      return response
    }

    return runFetch(buildOptions(refreshed))
  }

  public async loginWithInviteKey(
    inviteKey?: string,
    tenantHostName?: string,
  ): Promise<IResponseData> {
    if (!inviteKey) {
      throw new Error(InfoCode.INVALID_INVITE_TOKEN.toString())
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Tenant-Name': Config.TENANT_ID,
    }
    // Lets the backend resolve the tenant when the request Origin is not a
    // tenant host (local-dev LAN IP) — it cannot be inferred from the host.
    if (tenantHostName) {
      headers.TenantHost = tenantHostName
    }

    const response = await fetch(this.authUrl + AuthEndpoints.INVITE_KEY, {
      method: 'POST',
      mode: 'cors',
      headers,
      body: JSON.stringify({ inviteKey }),
    })

    if (response.ok) {
      return ApiHelper.toResponseData(response)
    }

    throw new Error(response.status.toString())
  }

  /**
   * Exchange a single-use session handoff code (STX1-495). Pass the current
   * access token when a session exists: same account → `reused`, no new tokens.
   */
  public async exchangeHandoffCode(
    code: string,
    accessToken?: string,
  ): Promise<IHandoffCodeExchangeResult> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    }

    const controller = new AbortController()
    const timeout = setTimeout(
      () => controller.abort(),
      HANDOFF_CODE_EXCHANGE_TIMEOUT_MS,
    )

    try {
      const response = await fetch(
        this.authUrl + AuthEndpoints.HANDOFF_CODE_EXCHANGE,
        {
          method: 'POST',
          mode: 'cors',
          signal: controller.signal,
          headers,
          body: JSON.stringify({ code }),
        },
      )

      if (response.ok) {
        // Awaited here so the timeout also covers reading the body.
        return await response.json()
      }

      throw new Error(response.status.toString())
    } finally {
      clearTimeout(timeout)
    }
  }

  public async resetPassword(email: string): Promise<IResponseData> {
    const response = await fetch(this.authUrl + AuthEndpoints.RESET_PASSWORD, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })

    if (response.ok) {
      return (await ApiHelper.toResponseData(response)).data
    }

    throw new Error(response.status.toString())
  }

  public async validateResetCode(code: string): Promise<void> {
    const url =
      this.authUrl +
      AuthEndpoints.VALIDATE_RESET_CODE +
      `?code=${encodeURIComponent(code)}`
    const response = await fetchWithTransientRetry(() =>
      fetch(url, {
        method: 'GET',
        mode: 'cors',
      }),
    )

    if (response.ok) return
    if (isResetCodeRejected(response.status)) {
      throw new UserFacingError(
        Localization.translator.passwordResetLinkInvalid,
      )
    }
    throw new Error(
      `${AuthEndpoints.VALIDATE_RESET_CODE} failed with status ${response.status}`,
    )
  }

  public async confirmPasswordReset(
    validationCode: string,
    password: string,
  ): Promise<void> {
    const response = await fetch(
      this.authUrl + AuthEndpoints.RESET_PASSWORD_COMPLETE,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ validationCode, password }),
      },
    )

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw typeof body.error === 'string' &&
        isResetCodeRejected(response.status)
        ? new UserFacingError(body.error)
        : new Error('Failed to reset password')
    }
  }

  public async getAuthMe(accessToken: string): Promise<AuthMeResponse> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 5000)

    try {
      const response = await this.withRefreshRetry(
        this.authUrl + AuthEndpoints.ME,
        token => ({
          method: 'GET',
          mode: 'cors',
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            'Tenant-Name': Config.TENANT_ID,
          },
        }),
        accessToken,
      )

      if (response.ok) {
        const data = await response.json()
        return {
          hasPassword: !!data.hasPassword,
          hasEmail: !!data.hasEmail,
          isSSOEnforced: !!data.isSSOEnforced,
          isPasswordRequired: !!data.isPasswordRequired,
        }
      }

      throw new Error(`/auth/me check failed with status ${response.status}`)
    } finally {
      clearTimeout(timeout)
    }
  }

  /**
   * Fetch the user's cross-tenant project list for the User Portal. The auth
   * service fans out to every tenant in parallel and gives each up to 10s
   * before answering with partial results + `failedTenants`, so the client
   * must wait longer than that or it aborts just as the partial answer is due.
   */
  public async getPortalProjects(
    accessToken: string,
  ): Promise<IPortalProjectsResponse> {
    const controller = new AbortController()
    const timeout = setTimeout(
      () => controller.abort(),
      PORTAL_PROJECTS_TIMEOUT_MS,
    )

    try {
      const response = await this.withRefreshRetry(
        this.authUrl + AuthEndpoints.PORTAL_PROJECTS,
        token => ({
          method: 'GET',
          mode: 'cors',
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        accessToken,
      )

      if (response.ok) {
        const { data } = await ApiHelper.toResponseData(response)
        return data as IPortalProjectsResponse
      }

      throw new Error(
        `${AuthEndpoints.PORTAL_PROJECTS} failed with status ${response.status}`,
      )
    } finally {
      clearTimeout(timeout)
    }
  }

  /**
   * Fetch the signed-in user's profile for the User Portal.
   */
  public async getPortalProfile(
    accessToken: string,
  ): Promise<IPortalProfileResponse> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 10000)

    try {
      const response = await this.withRefreshRetry(
        this.authUrl + AuthEndpoints.PORTAL_PROFILE,
        token => ({
          method: 'GET',
          mode: 'cors',
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        accessToken,
      )

      if (response.ok) {
        const { data } = await ApiHelper.toResponseData(response)
        return data as IPortalProfileResponse
      }

      throw new Error(
        `${AuthEndpoints.PORTAL_PROFILE} failed with status ${response.status}`,
      )
    } finally {
      clearTimeout(timeout)
    }
  }

  /**
   * Update the signed-in user's profile for the User Portal.
   */
  public async patchPortalProfile(
    accessToken: string,
    patch: IPortalProfileUpdate,
  ): Promise<IPortalProfileResponse> {
    const controller = new AbortController()
    const timeout = setTimeout(
      () => controller.abort(),
      PORTAL_PROFILE_SAVE_TIMEOUT_MS,
    )

    try {
      const response = await this.withRefreshRetry(
        this.authUrl + AuthEndpoints.PORTAL_PROFILE,
        token => ({
          method: 'PATCH',
          mode: 'cors',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(patch),
        }),
        accessToken,
      )

      if (response.ok) {
        const { data } = await ApiHelper.toResponseData(response)
        return data as IPortalProfileResponse
      }

      const body = (await response.json().catch(() => ({}))) as {
        code?: string
        message?: string
      }
      const localized =
        body.code && PORTAL_PROFILE_ERROR_MESSAGES[body.code]?.()
      if (localized) throw new UserFacingError(localized)
      throw new Error(
        body.message ||
          `${AuthEndpoints.PORTAL_PROFILE} failed with status ${response.status}`,
      )
    } finally {
      clearTimeout(timeout)
    }
  }

  /**
   * Text a verification code to a phone the signed-in user wants on their profile.
   */
  public startPhoneVerification(
    accessToken: string,
    phoneNumber: string,
  ): Promise<IPhoneVerificationResponse> {
    return this.postPhoneVerification(
      AuthEndpoints.PHONE_VERIFICATION,
      accessToken,
      { phoneNumber },
    )
  }

  /**
   * Text a fresh code for a pending phone verification.
   */
  public resendPhoneVerification(
    accessToken: string,
    verificationId: string,
  ): Promise<IPhoneVerificationResponse> {
    return this.postPhoneVerification(
      `${AuthEndpoints.PHONE_VERIFICATION}/${encodeURIComponent(verificationId)}/resend`,
      accessToken,
    )
  }

  /**
   * Confirm a pending phone verification; on success the phone is saved as verified.
   */
  public confirmPhoneVerification(
    accessToken: string,
    verificationId: string,
    code: string,
  ): Promise<IPhoneVerificationConfirmResponse> {
    return this.postPhoneVerification(
      `${AuthEndpoints.PHONE_VERIFICATION}/${encodeURIComponent(verificationId)}/confirm`,
      accessToken,
      { code },
    )
  }

  private async postPhoneVerification<T>(
    path: string,
    accessToken: string,
    body?: Record<string, string>,
  ): Promise<T> {
    const controller = new AbortController()
    const timeout = setTimeout(
      () => controller.abort(),
      PHONE_VERIFICATION_TIMEOUT_MS,
    )

    try {
      const response = await this.withRefreshRetry(
        this.authUrl + path,
        token => ({
          method: 'POST',
          mode: 'cors',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: body ? JSON.stringify(body) : undefined,
        }),
        accessToken,
      )

      if (response.ok) {
        return (await response.json()) as T
      }

      const error = (await response
        .json()
        .catch(() => ({}))) as IPhoneVerificationErrorBody
      const localized =
        error.code && PHONE_VERIFICATION_ERROR_MESSAGES[error.code]?.(error)
      if (localized) {
        throw new UserFacingError(
          localized,
          error.code,
          error.retryAfterSeconds
            ? {
                afterSeconds: error.retryAfterSeconds,
                // An older auth service sends no scope: hold every send.
                scope: error.scope ?? 'account',
              }
            : undefined,
        )
      }
      throw new Error(`${path} failed with status ${response.status}`)
    } finally {
      clearTimeout(timeout)
    }
  }

  /**
   * Upload the signed-in user's avatar for the User Portal.
   */
  public async uploadPortalAvatar(
    accessToken: string,
    file: File,
  ): Promise<{ avatarUrl: string }> {
    const controller = new AbortController()
    const timeout = setTimeout(
      () => controller.abort(),
      PORTAL_AVATAR_UPLOAD_TIMEOUT_MS,
    )

    try {
      const response = await this.withRefreshRetry(
        this.authUrl + AuthEndpoints.PORTAL_AVATAR,
        token => {
          const form = new FormData()
          form.append('file', file)
          return {
            method: 'POST',
            mode: 'cors',
            signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: form,
          }
        },
        accessToken,
      )

      if (response.ok) {
        return await response.json()
      }

      const body = (await response.json().catch(() => ({}))) as {
        message?: string
      }
      throw new Error(
        body.message ||
          `${AuthEndpoints.PORTAL_AVATAR} failed with status ${response.status}`,
      )
    } finally {
      clearTimeout(timeout)
    }
  }

  /**
   * Remove the signed-in user's avatar for the User Portal (204, no body).
   * Saving-length timeout: auth also clears it on the linked tenant users.
   */
  public async removePortalAvatar(accessToken: string): Promise<void> {
    const controller = new AbortController()
    const timeout = setTimeout(
      () => controller.abort(),
      PORTAL_PROFILE_SAVE_TIMEOUT_MS,
    )

    try {
      const response = await this.withRefreshRetry(
        this.authUrl + AuthEndpoints.PORTAL_AVATAR,
        token => ({
          method: 'DELETE',
          mode: 'cors',
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        accessToken,
      )

      if (response.ok) return

      const body = (await response.json().catch(() => ({}))) as {
        message?: string
      }
      throw new Error(
        body.message ||
          `${AuthEndpoints.PORTAL_AVATAR} failed with status ${response.status}`,
      )
    } finally {
      clearTimeout(timeout)
    }
  }

  /**
   * Submit the portal's "New project" request; the auth service emails it to
   * the monitored StruxHub inbox.
   */
  public async submitPortalProjectRequest(
    accessToken: string,
    request: IPortalProjectRequest,
  ): Promise<void> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 15000)

    try {
      const response = await this.withRefreshRetry(
        this.authUrl + AuthEndpoints.PORTAL_PROJECT_REQUEST,
        token => ({
          method: 'POST',
          mode: 'cors',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(request),
        }),
        accessToken,
      )

      if (response.ok) return

      const t = Localization.translator.portalProjectRequest
      throw new UserFacingError(
        response.status === 429
          ? t.errorTooManyRequests
          : response.status === 400
            ? t.errorInvalid
            : t.errorSubmit,
      )
    } finally {
      clearTimeout(timeout)
    }
  }

  /**
   * Fire-and-forget: stamps LastOpenedAt on the UserProject in the target
   * tenant. The caller navigates away right after, so the request is sent
   * with `keepalive` to survive the page unload.
   */
  public stampProjectOpened(
    accessToken: string,
    tenantHost: string,
    projectId: string,
  ): void {
    this.withRefreshRetry(
      this.authUrl + AuthEndpoints.PORTAL_STAMP_OPENED,
      token => ({
        method: 'POST',
        keepalive: true,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tenantHost, projectId }),
      }),
      accessToken,
    ).catch(() => {
      // Best-effort; failure doesn't affect the user flow.
    })
  }

  public async setPassword(
    password: string,
    accessToken: string,
    currentPassword?: string,
  ): Promise<IResponseData> {
    // Bounded like getAuthMe: the prompt closes every exit while this is in
    // flight, so a request that never settles would leave the user in a modal
    // they cannot dismiss. A timeout turns that into an error they can act on.
    const controller = new AbortController()
    const timeout = setTimeout(
      () => controller.abort(),
      SET_PASSWORD_TIMEOUT_MS,
    )

    let response: Response
    try {
      response = await this.withRefreshRetry(
        this.authUrl + AuthEndpoints.SET_PASSWORD,
        token => ({
          method: 'POST',
          mode: 'cors',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            password,
            ...(currentPassword ? { currentPassword } : {}),
          }),
        }),
        accessToken,
      )
    } finally {
      clearTimeout(timeout)
    }

    if (response.ok) {
      return await ApiHelper.toResponseData(response)
    }

    if (response.status === 401 || response.status === 403) {
      throw new UserFacingError(Localization.translator.ssoSessionExpired)
    }

    let message: string
    let code: string | undefined
    try {
      const body = await response.json()
      code = typeof body?.code === 'string' ? body.code : undefined
      message =
        SET_PASSWORD_ERROR_MESSAGES[body?.code]?.() ||
        body?.errors?.[0]?.message ||
        body?.message ||
        body?.title
    } catch {
      // response body is not JSON
    }

    throw new UserFacingError(
      message || Localization.translator.somethingWentWrongDuringAPIInteraction,
      code,
    )
  }

  public async getInviteKey(
    accessToken: string,
    tenantHostName?: string,
  ): Promise<string> {
    const response = await this.withRefreshRetry(
      this.authUrl + AuthEndpoints.INVITE_KEY,
      token => {
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          'Tenant-Name': Config.TENANT_ID,
        }
        if (tenantHostName) {
          headers.TenantHost = tenantHostName
        }
        return { method: 'GET', headers }
      },
      accessToken,
    )

    if (response.ok) {
      return (await ApiHelper.toResponseData(response)).data
    }

    throw new Error(response.status.toString())
  }

  /**
   * Mint a single-use session handoff code for the current session (STX1-495).
   * `targetHost` null = the login host (User Portal).
   */
  public async mintHandoffCode(
    accessToken: string,
    targetHost?: string,
  ): Promise<string> {
    const controller = new AbortController()
    const timeout = setTimeout(
      () => controller.abort(),
      HANDOFF_CODE_MINT_TIMEOUT_MS,
    )

    try {
      const response = await this.withRefreshRetry(
        this.authUrl + AuthEndpoints.HANDOFF_CODE,
        token => ({
          method: 'POST',
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ targetHost: targetHost ?? null }),
        }),
        accessToken,
      )

      if (!response.ok) {
        throw new Error(response.status.toString())
      }
      // Callers fall back to the invite key only on a throw, so an empty code must throw.
      const { code } = await response.json()
      if (!code) {
        throw new Error('Handoff code response had no code')
      }
      return code
    } finally {
      clearTimeout(timeout)
    }
  }

  public async checkUserByPhoneNumber(phoneNumber: string): Promise<boolean> {
    const res = await this.graphExecutor.executeQuery(
      CheckUserByPhoneNumberDocument,
      { phoneNumber },
    )

    return !!res.data?.checkPhoneNumber
  }

  public async resendInviteKey(expiredInviteKey: string): Promise<void> {
    const res = await this.graphExecutor.executeMutation(
      ResendInviteKeyDocument,
      { inviteKey: expiredInviteKey, projectCode: this.state.initProjectCode },
    )

    if (!res.data?.resendInviteKey) {
      throw new Error(
        Localization.translator.somethingWentWrongDuringAPIInteraction,
      )
    }
  }

  //#region Centralized Login Auth
  public setRefreshAccessTokenDelegate(delegate: () => Promise<string>): void {
    this.refreshAccessTokenDelegate = delegate
  }

  /**
   * Drive a refresh through the single authority (StruxHubAuthService). There
   * is intentionally no fallback refresh path: a second, lock-less refresh here
   * is the very race STX1-616 fixes, so an unwired delegate is a wiring bug, not
   * a runtime branch to silently paper over. Every container wires it in its
   * constructor before any request can fire.
   */
  @action.bound
  public updateAccessToken(): Promise<string> {
    if (!this.refreshAccessTokenDelegate) {
      throw new Error(
        'ApiAuthService.updateAccessToken called before the refresh delegate was wired',
      )
    }
    return this.refreshAccessTokenDelegate()
  }

  public getValidAccessToken = async (): Promise<string> => {
    if (!this.accessToken) {
      this.setAccessToken(this.state.tenantUserSession?.accessToken)
    }

    if (this.accessToken && isTokenAboutToExpire(this.accessToken)) {
      return this.updateAccessToken()
    }
    return this.accessToken
  }

  @action.bound
  public setAccessToken(token: string) {
    this.accessToken = token
  }

  @action.bound
  public resetAccessToken() {
    this.accessToken = null
  }

  public async sendOneTimeCode(phoneNumber: string): Promise<IOneTimeCode> {
    const url = this.authUrl + '/send-otc'
    const body = JSON.stringify({ phoneNumber })
    const headers = {
      'Content-Type': 'application/json;charset=utf-8',
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body,
    })
    if (response.ok) {
      return response.json()
    }

    if (response?.status >= 400) {
      const responseTxt = await response.text()
      if (LOGIN_METHOD_NOT_ALLOWED_REGEX.test(responseTxt)) {
        throw new UserFacingError(Localization.translator.ssoProviderNotAllowed)
      }
      if (
        !responseTxt ||
        responseTxt.includes(INVALID_PHONE_NUMBER) ||
        !!responseTxt.match(PHONE_NUMBER_NOT_FOUND_REGEX)
      ) {
        const errorMsg = `sendOneTimeCode failed with ${response.status} HTTP status code and the following message: ${responseTxt}`
        console.error(errorMsg)
      }
    }
    return null
  }

  public logInViaStruxhub(
    authData: EmailAuth | PhoneAuth,
  ): Promise<IAuthenticateResult> {
    return this.authenticate(authData, '/authenticate', {
      // One message for a wrong password and an unknown email (STX1-1335).
      unauthorizedMessage:
        authData.grantType === GrantTypesEnum.password
          ? () => Localization.translator.incorrectEmailOrPassword
          : undefined,
    })
  }

  public getFreshStruxhubTokens(
    authData: IFreshToken,
  ): Promise<IAuthenticateResult> {
    return this.authenticate(authData, '/authenticate', {
      timeoutMs: REFRESH_REQUEST_TIMEOUT_MS,
      retryTransient: true,
    })
  }

  private async authenticate(
    data: IAuth | IFreshToken,
    path: string,
    options: IAuthenticateOptions = {},
  ): Promise<IAuthenticateResult> {
    const {
      additionalHeaders,
      timeoutMs,
      retryTransient,
      unauthorizedMessage,
    } = options
    const controller = timeoutMs ? new AbortController() : undefined
    const timeout = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : undefined
    try {
      const doFetch = (): Promise<Response> =>
        fetch(this.authUrl + path, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json;charset=utf-8',
            ...additionalHeaders,
          },
          body: JSON.stringify(data),
          signal: controller?.signal,
        })

      // Passing the timeout's own signal keeps the abort authoritative: a fired
      // REFRESH_REQUEST_TIMEOUT_MS propagates instead of being retried, and cuts
      // short an in-flight backoff, so the retry loop cannot outlive the bound
      // the cross-tab Web Lock relies on.
      const response = retryTransient
        ? await fetchWithTransientRetry(doFetch, {
            maxAttempts: REFRESH_RETRY_MAX_ATTEMPTS,
            baseDelayMs: REFRESH_RETRY_BASE_DELAY_MS,
            signal: controller?.signal,
          })
        : await doFetch()

      if (response.ok) {
        const result = await response.json()
        return result
      }

      if (response.status === 401 && unauthorizedMessage) {
        throw new UserFacingError(unauthorizedMessage())
      }

      return null
    } finally {
      if (timeout) clearTimeout(timeout)
    }
  }

  public async getTenantsForUser(accessToken: string): Promise<Array<ITenant>> {
    const tenantsResponse = await this.withRefreshRetry(
      this.authUrl + '/tenants',
      token => ({
        method: 'GET',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
          Authorization: `Bearer ${token}`,
        },
      }),
      accessToken,
    )
    if (!tenantsResponse.ok) {
      throw new Error(tenantsResponse.status.toString())
    }

    const result = await tenantsResponse.json()
    const tenants: ITenant[] = []
    for (const [key, value] of Object.entries(result)) {
      tenants.push({ name: key, host: value as string })
    }

    // Allow testing login bundle on localhost. The synthetic tenant's
    // host is the local desktop or mobile bundle port (NOT the login
    // port), so the post-login Redirector hands off to the actual app
    // bundle running on localhost:3000 / localhost:3001 — which then
    // bootstraps with the inviteKey and renders the project. Pointing
    // back at the login port loops on the login bundle's loader.
    if (isLoginBundle() && location.hostname === 'localhost') {
      const useMobile = isMobile || isTablet
      const targetPort = useMobile
        ? configuredMobilePort
        : configuredDesktopPort
      tenants.push({
        name: useMobile ? 'Local Mobile' : 'Local Desktop',
        host: `localhost:${targetPort}`,
      })
    }

    return tenants
  }

  /**
   * Initiate Microsoft SSO login flow via Auth service.
   * Returns the OAuth URL to redirect the user to.
   */
  public async initiateMicrosoftSSO(
    redirectUri?: string,
    tenantHint?: string,
  ): Promise<ISSOInitResponse> {
    return this.initiateSSO('microsoft', redirectUri, tenantHint)
  }

  /**
   * Initiate Procore SSO login flow via Auth service.
   * Returns the OAuth URL to redirect the user to.
   */
  public async initiateProcoreSSO(
    redirectUri?: string,
    tenantHint?: string,
  ): Promise<ISSOInitResponse> {
    return this.initiateSSO('procore', redirectUri, tenantHint)
  }

  /**
   * Initiate Google SSO login flow via Auth service.
   * Returns the OAuth URL to redirect the user to.
   */
  public async initiateGoogleSSO(
    redirectUri?: string,
    tenantHint?: string,
  ): Promise<ISSOInitResponse> {
    return this.initiateSSO('google', redirectUri, tenantHint)
  }

  /**
   * Generic SSO initiation via Auth service.
   *
   * The post-login destination (STX1-540) rides on `redirectUri` as a
   * `?return_to=` query param — the auth service stores the full
   * redirectUri server-side keyed by the random, single-use `state` and
   * echoes it back on the /sso-callback redirect. `return_to` therefore
   * never reaches the IdP (only the opaque `state` does), and no
   * separate request field is needed.
   */
  private async initiateSSO(
    provider: string,
    redirectUri?: string,
    tenantHint?: string,
  ): Promise<ISSOInitResponse> {
    const url = this.authUrl + `/sso/${provider}`
    const body = JSON.stringify({
      provider,
      redirectUri,
      tenantHint,
    })
    const headers = {
      'Content-Type': 'application/json;charset=utf-8',
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body,
    })

    if (response.ok) {
      return response.json()
    }

    const errorText = await response.text()
    throw new Error(`Failed to initiate ${provider} SSO: ${errorText}`)
  }

  /**
   * Fetch allowed login providers for a given email domain.
   * Returns union across all tenants. Null means no restrictions.
   */
  public async fetchDomainProviders(domain: string): Promise<string[] | null> {
    const url = `${this.authUrl}/providers?domain=${encodeURIComponent(domain)}`
    const response = await fetchWithTransientRetry(() =>
      fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
    )

    if (response.ok) {
      const result = await response.json()
      return result.providers ?? null
    }

    return null
  }

  /**
   * Exchange temporary SSO code for authentication result.
   * Called when Zoomin receives a callback with sso_code after OAuth flow.
   */
  public async exchangeSSOCode(ssoCode: string): Promise<ISSOCallbackResult> {
    const url = `${this.authUrl}/sso/result?code=${encodeURIComponent(ssoCode)}`

    const response = await fetchWithTransientRetry(() =>
      fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
        },
      }),
    )

    const result = await response.json()

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'SSO authentication failed')
    }

    return result
  }
}

interface ISuccessfulOneTimeCodeResponse {
  nonsense: string
  error?: never
}
interface IFailedOneTimeCodeResponse {
  error: number | string
  nonsense?: never
}
type IOneTimeCode =
  | ISuccessfulOneTimeCodeResponse
  | IFailedOneTimeCodeResponse
  | null

//#endregion
